import type { FormikErrors } from 'formik';
import { Form, Formik } from 'formik';

import type { BudgetMessage } from '../../../../types/BudgetMessage';
import { Button } from '../../../cdl/Button/Button';
import { DrawerContentLayout } from '../../../cdl/Drawer/DrawerContentLayout';
import { Text } from '../../../cdl/Text/Text';
import { FormikDatePicker } from '../../../common/form-elements/formik/FormikDatePicker';
import { FormikDebug } from '../../../common/form-elements/formik/FormikDebug';
import { translate } from '../../../common/helpers/translate.helper';
import { FormikMoneyInput } from '../../../common/MoneyInput/FormikMoneyInput';

export interface VesselBudgetFormValues {
    budget: {
        amount: string;
    };
    startDate: string;
}

interface BudgetFormProps {
    budgetData?: BudgetMessage;
    onSubmit: (values: VesselBudgetFormValues) => void | Promise<void>;
    primaryActionText: string;
}

const getInitialValues = (budgetData?: BudgetMessage): VesselBudgetFormValues => ({
    budget: {
        amount: budgetData ? String(budgetData.budget.amount) : '',
    },
    startDate: budgetData?.startDate ?? '',
});

export const BudgetForm = ({ budgetData, onSubmit, primaryActionText }: BudgetFormProps) => {
    const validate = (values: VesselBudgetFormValues) => {
        const errors: FormikErrors<VesselBudgetFormValues> = {};

        if (!values.budget.amount) {
            errors.budget = { amount: translate('errormessage.empty') };
        }

        if (!values.startDate) {
            errors.startDate = translate('errormessage.empty');
        }

        return errors;
    };

    return (
        <Formik initialValues={getInitialValues(budgetData)} validate={validate} validateOnMount onSubmit={onSubmit}>
            {({ isSubmitting, dirty }) => (
                <Form style={{ height: '100%', minHeight: 0 }}>
                    <DrawerContentLayout
                        isBusy={isSubmitting}
                        hasUnsavedChanges={dirty}
                        primaryAction={
                            <Button type="submit" emphasis="high" isLoading={isSubmitting}>
                                {primaryActionText}
                            </Button>
                        }
                    >
                        {budgetData ? <Text>{translate('budgets.updateInfo')} </Text> : null}

                        <FormikMoneyInput
                            name="budget.amount"
                            label={translate('budgets.budget')}
                            maxLength={7}
                            step={1000}
                            decimals={0}
                            allowDecimals={false}
                            allowNegativeValue={false}
                            markAsRequired
                            autoFocus
                        />

                        <FormikDatePicker name="startDate" label={translate('budgets.startDate')} markAsRequired />

                        <FormikDebug />
                    </DrawerContentLayout>
                </Form>
            )}
        </Formik>
    );
};
